import { useAlarmConverter } from '@services/alarm'
import { useConvertData } from '@services/device'
import {
  IChargingPointConsumption,
  IChargingPointOccupancy,
  TYPE,
  useReportQuery,
  xAxisName,
} from '@services/report'
import { useConvertParkingLotFullSession } from '@services/session'
import {
  convertData,
  QueryVariables,
  useLazyQuery,
} from '@shared/innmaxLib/services'
import {
  formatDate,
  isNil,
  momentToString,
  toThousandSeparator,
} from '@shared/utils/webHelper'
import { chain, map, path, pathOr, pipe, prop, values } from 'ramda'
import React from 'react'
import { useQuery, UseQueryOptions } from 'react-query'
import {
  useConvertAirportOperationsRpt,
  useConvertAirportOperationsSummary,
} from './newReport.converter'

export const useOperationsRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/operation', query],
    {
      ...options,
      enabled: !!query.reportType,
    }
  )

  const { sessionName, locationNs } = useConvertData()

  const _data: any = path(['data'], data)
  const { operationData, ...otherData } = _data || {}
  const contentData = React.useMemo(() => {
    return convertData(
      {
        field: 'operationData',
        converter: (x: any) => ({
          ...x,
          ...locationNs(x),
          ...sessionName(x),
        }),
      },
      _data
    )
  }, [_data])

  const dataSource = React.useMemo(() => {
    return {
      ...otherData,
      content: contentData?.content || [],
    }
  }, [contentData])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useOperationsRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/report/operation/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useParkingStatisticsRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/parking', query],
    {
      ...options,
      enabled: !!query.reportType && !!query.sessionId,
    }
  )

  const { sessionName, locationNs } = useConvertData()
  const _data: any = path(['data'], data)
  const { parkingData, ...otherData } = _data || {}
  const contentData = React.useMemo(() => {
    return convertData(
      {
        field: 'parkingData',
        converter: (x: any) => ({
          ...x,
          turnoverRate: x?.turnoverRate ? x.turnoverRate * 100 : 0,
          usageRate: x?.usageRate ? x.usageRate * 100 : 0,
          ...locationNs(x),
          ...sessionName(x),
        }),
      },
      _data
    )
  }, [_data])

  const dataSource = React.useMemo(() => {
    return {
      ...otherData,
      content: contentData?.content || [],
    }
  }, [contentData])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useParkingStatisticsRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/report/parking/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const ProblemCarEnum = [
  { value: 1, name: '失竊' },
  { value: 2, name: '註銷' },
  { value: 3, name: '列管' },
  { value: 4, name: '超時' },
  { value: 5, name: '違停' },
]

export const useProblemCarRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useReportQuery(
    '/report/problemVehicle/{pageNum}/{pageSize}',
    query,
    {
      ...options,
    }
  )

  const convertSessions = useConvertParkingLotFullSession()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: (x: any) => {
          const { parkingLotSession, distSession, citySession } =
            convertSessions(x, 'sessionId')
          return {
            ...x,
            reportDateTimeStr: momentToString(
              x.reportDateTime,
              'YYYY/MM/DD HH:mm:ss'
            ),
            parkingStartTimeStr: momentToString(
              x.parkingStartTime,
              'YYYY/MM/DD HH:mm:ss'
            ),
            parkingEndTimeStr: momentToString(
              x.parkingEndTime,
              'YYYY/MM/DD HH:mm:ss'
            ),
            parkingLotName: parkingLotSession?.name || '',
            districtName: distSession?.name || '',
            cityName: citySession?.name || '',
            problemTypeStr:
              ProblemCarEnum.find(item => item.value === x.problemType)?.name ||
              '',
          }
        },
      },
      data
    )
  }, [data, convertSessions])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useProblemCarRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(
    ['/report/problemVehicle/export', { ...query }],
    {
      ...options,
      enabled,
      onSuccess: (response: any) => {
        if (options?.onSuccess) {
          options.onSuccess(response)
        }
        setIsEnabled(false)
      },
      onError: error => {
        console.log(error)
        setIsEnabled(false)
      },
    }
  )

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useParkingVideoRecordRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    [
      '/parkingRecognition/report/parkingRecognition/{pageNum}/{pageSize}',
      query,
    ],
    {
      ...options,
      enabled: !!query.start && !!query.end && !!query.sessionId,
    }
  )

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: (x: any) => {
          return {
            ...x,
            inTimeStr: momentToString(x.inTime, 'YYYY/MM/DD HH:mm:ss'),
            outTimeStr: momentToString(x.outTime, 'YYYY/MM/DD HH:mm:ss'),
          }
        },
      },
      data
    )
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useLazyQueryParkingVideoRecordImage = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useLazyQuery(
    ['/parkingRecognition/images', query],
    {
      ...options,
    }
  )

  return {
    ...others,
    refetch,
    loading: isLoading,
    data,
  }
}

export const useAlarmV2Rpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/alarm/search', query],
    {
      ...options,
    }
  )

  const convertAlarm = useAlarmConverter()
  const convertSessions = useConvertParkingLotFullSession()

  const dataSource = React.useMemo(() => {
    return convertData(
      {
        converter: (d: any) => {
          const { parkingLotSession, distSession, citySession } =
            convertSessions(d, 'sessionId')
          return {
            ...convertAlarm(d),
            parkingLotStr: parkingLotSession?.name || '',
            districtStr: distSession?.name || '',
            cityStr: citySession?.name || '',
          }
        },
      },
      data
    )
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

type IEnergyResponse = {
  data: [
    {
      chargePointId: string
      connectorId: string
      degree: number
      electricMeterId: string
    }
  ]
  date: string
}

export const useEnergyRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/consumption', query],
    {
      ...options,
      enabled: !!query.reportType,
    }
  )

  const dataSource = React.useMemo(() => {
    const _data: IEnergyResponse[] = path(['data'], data) || []
    if (!_data) {
      return {
        content: [],
        total: 0,
        totalPages: 0,
      }
    }
    const groupedMap = new Map<string, any>()

    _data.forEach(item => {
      item.data.forEach(d => {
        const key = `${d.chargePointId}_${d.connectorId}_${d.electricMeterId}`
        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            chargePointId: d.chargePointId,
            connectorId: d.connectorId,
            electricMeterId: d.electricMeterId,
            total: 0,
          })
        }
        const group = groupedMap.get(key)
        group[item.date] = d.degree
        group.total += d.degree
        group.total = parseFloat(group.total.toFixed(2))
      })
    })

    const content = Array.from(groupedMap.values())

    return {
      content,
      total: content?.length || 0,
      totalPages: 0,
    }
  }, [data])

  const cols = React.useMemo(() => {
    const _data: Array<{ date: string; data: Array<any> }> = (data as any)
      ?.data as any
    return (
      (_data?.map((ele: any) => ({
        title: ele?.date,
        dataIndex: ele?.date,
        align: 'center',
        render: (v: any) => toThousandSeparator(v),
      })) as any) || []
    )
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
    cols,
  }
}

export const useEnergyRptExport = (query?: any, options?: QueryVariables) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/report/consumption/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useChargeServiceRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/charge', query],
    {
      ...options,
      enabled: !!query.reportType && !!query.sessionId,
    }
  )

  const { sessionName, locationNs } = useConvertData()

  const _data: any = path(['data'], data)
  const { chargeData, ...otherData } = _data || {}
  const contentData = React.useMemo(() => {
    return convertData(
      {
        field: 'chargeData',
        converter: (x: any) => ({
          ...x,
          ...locationNs(x),
          ...sessionName(x),
          usageRate: isNil(x.usageRate) ? 0 : x.usageRate * 100,
          turnoverRate: isNil(x.turnoverRate) ? 0 : x.turnoverRate * 100,
          failureRate: isNil(x.failureRate) ? 0 : x.failureRate * 100,
          properRate: isNil(x.properRate) ? 0 : x.properRate * 100,
        }),
      },
      _data
    )
  }, [_data])

  const dataSource = React.useMemo(() => {
    return {
      ...otherData,
      content: contentData?.content || [],
    }
  }, [contentData])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useChargeServiceRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(['/report/charge/export', { ...query }], {
    ...options,
    enabled,
    onSuccess: (response: any) => {
      if (options?.onSuccess) {
        options.onSuccess(response)
      }
      setIsEnabled(false)
    },
    onError: error => {
      console.log(error)
      setIsEnabled(false)
    },
  })

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useChargingPointRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/chargingpoint', query],
    {
      cacheTime: 0,
      select: (res: any) => path(['data'], res),
      ...options,
    }
  ) as any

  const { sessionName } = useConvertData()

  const dataSource = React.useMemo(() => {
    return {
      ...data,
      detailList: values(data?.detailList)?.map((item: any) => ({
        ...item,
        ...sessionName(item),
      })),
    }
  }, [data, sessionName])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useConnectorStatusRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/connectorStatus', query],
    {
      cacheTime: 0,
      select: (res: any) => path(['data'], res),
      ...options,
    }
  ) as any

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource: data,
  }
}

export const useIncomeRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/income', query],
    {
      cacheTime: 0,
      select: (res: any) => path(['data'], res),
      ...options,
    }
  ) as any

  const { sessionName, locationNs } = useConvertData()

  const dataSource = React.useMemo(() => {
    return {
      ...data,
      incomeList: data?.incomeList?.map((item: any) => ({
        ...item,
        ...sessionName(item),
        ...locationNs(item),
      })),
    }
  }, [data, sessionName, locationNs])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useIncomeTrendRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/income/trend', query],
    {
      cacheTime: 0,
      select: (res: any) => path(['data'], res),
      ...options,
    }
  ) as any

  const dataSource = React.useMemo(() => {
    if (!data) {
      return []
    }
    return pipe(
      // chain(({ date, chargeAmount, parkingAmount, occupyAmount }) => [
      //   { date, type: 'chargeAmount', value: Math.round(Math.random() * 10) },
      //   { date, type: 'parkingAmount', value: Math.round(Math.random() * 10) },
      //   { date, type: 'occupyAmount', value: Math.round(Math.random() * 10) },
      // ])
      chain(({ date, chargeAmount, parkingAmount, occupyAmount }) => [
        { date, type: 'chargeAmount', value: chargeAmount },
        { date, type: 'occupyAmount', value: occupyAmount },
        { date, type: 'parkingAmount', value: parkingAmount },
      ])
    )(data)
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useChargeTrendRpt = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/charge/trend', query],
    {
      cacheTime: 0,
      ...options,
    }
  ) as any

  const dataSource = React.useMemo(() => {
    if (isNil(data)) {
      return {}
    }

    const { reportType } = query

    const contentData = (pathOr([], ['data'], data) as any).map((i: any) => {
      let displayDate: any = ''
      switch (reportType) {
        case TYPE.YEAR:
          const m = new Date(i.date).getMonth()
          displayDate = xAxisName(m)
          break
        case TYPE.MONTH:
          displayDate = `${new Date(i.date).getDate()}日`
          break
        case TYPE.DAY:
          displayDate = formatDate(new Date(`${i.date}:00:00`).valueOf(), 'HH')
          break
        default:
          break
      }
      return { ...i, displayDate }
    })

    const total = contentData.length
    const chargeAmount = map(prop('chargeAmount'), contentData || [])
    const chargeDegree = map(prop('chargeDegree'), contentData || [])

    // const chargeAmount = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    // const chargeDegree = [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1]

    return {
      xAxisLabels: contentData.map((i: any) => i.displayDate),
      total,
      chargeAmount,
      chargeDegree,
    }
  }, [data, query])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useChargingPointConsumptionRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/chargingpoint/consumption', query],
    {
      select: (res: any) => path(['data'], res),
      keepPreviousData: true,
      ...options,
    }
  ) as any

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource: data as IChargingPointConsumption | undefined,
  }
}

export const useChargingPointOccupancyRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/chargingpoint/occupancy', query],
    {
      select: (res: any) => path(['data'], res),
      keepPreviousData: true,
      ...options,
    }
  ) as any

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource: data as IChargingPointOccupancy | undefined,
  }
}

export const useAirportOperationsSummaryRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useLazyQuery(
    ['/report/airportOperation/summary', query],
    options
  )

  const converter = useConvertAirportOperationsSummary()

  const dataSource = React.useMemo(() => {
    return converter(path(['data'], data))
  }, [converter, data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    data: dataSource,
  }
}

export const useAirportOperationsRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/airportOperation/{pageNum}/{pageSize}', query],
    options
  )

  const converter = useConvertAirportOperationsRpt()

  const dataSource = React.useMemo(() => {
    return convertData({
      field: ['data'],
      converter,
    })(data)
  }, [converter, data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useAirportOperationsRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(
    ['/report/airportOperation/export', { ...query }],
    {
      ...options,
      enabled,
      onSuccess: (response: any) => {
        if (options?.onSuccess) {
          options.onSuccess(response)
        }
        setIsEnabled(false)
      },
      onError: error => {
        console.log(error)
        setIsEnabled(false)
      },
    }
  )

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useAirportConsumptionRpt = (
  query?: any,
  options?: UseQueryOptions
) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/airportConsumption/{pageNum}/{pageSize}', query],
    options
  )

  const dataSource = React.useMemo(() => {
    return convertData({
      field: ['data'],
    })(data)
  }, [data])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}

export const useAirportConsumptionRptExport = (
  query?: any,
  options?: QueryVariables
) => {
  const [enabled, setIsEnabled] = React.useState(false)

  const { isLoading } = useQuery(
    ['/report/airportConsumption/export', { ...query }],
    {
      ...options,
      enabled,
      onSuccess: (response: any) => {
        if (options?.onSuccess) {
          options.onSuccess(response)
        }
        setIsEnabled(false)
      },
      onError: error => {
        console.log(error)
        setIsEnabled(false)
      },
    }
  )

  return [
    isLoading,
    () => {
      setIsEnabled(true)
    },
  ] as [boolean, () => void]
}

export const useProperRate = (query?: any, options?: UseQueryOptions) => {
  const { data, refetch, isLoading, ...others } = useQuery(
    ['/report/properRate', query],
    {
      cacheTime: 0,
      ...options,
    }
  ) as any

  const dataSource = React.useMemo(() => {
    if (isNil(data)) {
      return {}
    }

    const { reportType } = query

    const contentData = (pathOr([], ['data'], data) as any).map((i: any) => {
      let displayDate: any = ''
      switch (reportType) {
        case TYPE.YEAR:
          const m = new Date(i.date).getMonth()
          displayDate = xAxisName(m)
          break
        case TYPE.MONTH:
          displayDate = `${new Date(i.date).getDate()}日`
          break
        case TYPE.DAY:
          displayDate = formatDate(new Date(`${i.date}:00:00`).valueOf(), 'HH')
          break
        default:
          break
      }
      return { ...i, displayDate }
    })

    const total = contentData.length
    const offlineDuration = pipe(
      map(prop('offlineDuration')),
      map((val: any) => Math.floor((val || 0) / 3600000))
    )(contentData || [])
    const properRate = pipe(
      map(prop('properRate')),
      map((val: any) => (val || 0) * 100)
    )(contentData || [])
    const totalDuration = pipe(
      map(prop('totalDuration')),
      map((val: any) => Math.floor((val || 0) / 3600000))
    )(contentData || [])

    return {
      xAxisLabels: contentData.map((i: any) => i.displayDate),
      total,
      offlineDuration,
      properRate,
      totalDuration,
    }
  }, [data, query])

  return {
    ...others,
    refetch,
    loading: isLoading,
    dataSource,
  }
}
